/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { NavLink, useResolvedPath } from 'react-router';

import { Badge, Tab, Tabs, Typography } from '@mui/material';

import { DeletableThumbnailBadge } from '../../components/DeletableThumbnailBadge/DeletableThumbnailBadge';
import { EntityThumbnail } from '../../components/EntityThumbnail/EntityThumbnail';
import { SimpleConfirmModal } from '../../components/SimpleConfirmModal/SimpleConfirmModal';
import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { UserApiService } from '../../services/UserApiService';
import { RootState } from '../../store';
import { useBadgeStyles } from '../../styles/badge';
import { useNavTabsStyles } from '../../styles/navigationTabs';
import { ApiClientError } from '../../types/api/ApiClientError';
import { readFile } from '../../utils/canvasUtils';
import { getUserThumbnailVersion } from '../../utils/getUserThumbnailVersion';
import { reactToUpdateUserThumbnail } from '../../utils/reactions';
import { MainLayout } from '../MainLayout/MainLayout';
import { useProfileLayoutStyles } from './ProfileLayout.styles';

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
});

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type ProfileLayoutProps = PropsFromRedux & {
  children: any;
};

const ProfileLayoutComponent = memo<ProfileLayoutProps>(props => {
  const { t } = useTranslation();
  const tNs = Namespaces.UserProfile;

  const { children, auth } = props;

  const classes = useProfileLayoutStyles();
  const badgeClasses = useBadgeStyles();
  const navTabsClasses = useNavTabsStyles();
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const onImageUpload = async (blobImage: Blob) => {
    setIsDisabled(true);
    try {
      await UserApiService.putUserThumbnail({ body: { type: 'base64', base64: await readFile(blobImage) } });
      await reactToUpdateUserThumbnail();
    } catch (e) {
      const error = e as ApiClientError;
      console.log(error);
    } finally {
      setIsDisabled(false);
    }
  };

  const onConfirmDeleteImage = async () => {
    setIsOpenModal(false);
    setIsDisabled(true);
    try {
      await UserApiService.deleteUserThumbnail();
      await reactToUpdateUserThumbnail();
    } catch (e) {
      const error = e as ApiClientError;
      console.log(error);
    } finally {
      setIsDisabled(false);
    }
  };

  const hideDeleteAvatarBtn = useMemo(() => !!auth.user?._links.thumbnail, [auth.user]);

  const thumbnailVersion = useMemo(() => getUserThumbnailVersion(auth.user), [auth.user]);

  const baseUrl = useResolvedPath('').pathname;

  return (
    <MainLayout>
      <div className={classes.profileTitle}>
        <DeletableThumbnailBadge
          overlap="circular"
          invisible={!hideDeleteAvatarBtn}
          onDelBtnClick={() => setIsOpenModal(true)}
          disabled={isDisabled}
          thumbnail={
            <EntityThumbnail
              allowUploadImage={true}
              onImageUpload={onImageUpload}
              size={50}
              type="user"
              className={classes.profilePhoto}
              mergeVersion={true}
              version={thumbnailVersion}
            />
          }
        />
        <Typography classes={{ h2: classes.pageTitle }} variant="h2">
          {t(`${tNs}:page_title`)}
        </Typography>
      </div>
      <Tabs
        aria-label="profile user tabs"
        indicatorColor="primary"
        scrollButtons={false}
        value={baseUrl}
        classes={{ root: navTabsClasses.root }}
        className={classes.tabsRoot}
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        <Tab
          value={BasicRoutes.PROFILE}
          label={t(`${tNs}:profile.title`)}
          component={NavLink}
          to={BasicRoutes.PROFILE}
        />
        <Tab
          value={BasicRoutes.ORGANIZATIONS}
          label={
            <Badge classes={{ badge: badgeClasses.textBadge }} variant="dot" invisible={!auth.user?.invitations.length}>
              {t(`${tNs}:organizations.title`)}
            </Badge>
          }
          component={NavLink}
          to={BasicRoutes.ORGANIZATIONS}
        />
      </Tabs>
      {children}
      <SimpleConfirmModal
        open={isOpenModal}
        message={t(`${tNs}:delete_image.confirm_delete_image`)}
        onCancel={() => setIsOpenModal(false)}
        onConfirm={onConfirmDeleteImage}
      />
    </MainLayout>
  );
});

export const ProfileLayout = connector(ProfileLayoutComponent);
